import React, { useState, FC } from "react";
import { isArray } from "lodash";
import { useTranslation } from "react-i18next";
import moment from "moment";
import useQuery from "Hooks/query.hook";
import { DataTable } from "@life-line-lab/components-react";
import { Col, Row, Divider, Spin } from "antd";
import {
  CaretDownOutlined,
  CaretUpOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import ReportFilters from "./report-filters";
import useSearch from "Hooks/search.hook";
import DataTableFilter from "Components/shared/data-table-filter";
import { getStatusStyle } from "Helpers/get-status-colours";
import { toCapitalisedSpaced } from "Helpers/to-capitalised-spaced";
import { formatDate } from "Helpers/format-date";
import { routeToEnrouteView } from "Helpers/route-enroute-view";
import { useHistory, useLocation } from "react-router-dom";

const findTrolleyData = (trolleyParcelData, trolleyID) => {
  if (!isArray(trolleyParcelData)) return null;
  if (!trolleyID) return null;

  return trolleyParcelData.find((parcelObj) => parcelObj._id == trolleyID);
};

const constructTrolleyCode = (item, referenceData) => {
  if (!isArray(item)) return "-";

  return item.map((curr, index) => {
    const trolley = findTrolleyData(referenceData, curr?.id);
    const trolleyCode = trolley?.trolleyCode;

    return <div key={index}>{trolleyCode ?? "-"}</div>;
  });
};

const TrainReportList: FC<any> = (props) => {
  const {
    onFilter,
    // onDropdownFilter,
    reportListing,
    handleDownloadExcelFile,
    itemPerPage,
    isLoading,
    handleRefreshList,
    isExcelLoading,
  } = props || {};

  const query = useQuery();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const page: any = query.get("page") || 1;

  const [filterState, setFilterState] = useState<any>({
    status: "",
    sortByCreatedAt: "DESC",
  });
  const { handleSearch } = useSearch({ onFilter, filterState, setFilterState });

  const tabKeyPaginationProps = {
    pathWithoutPage: {
      withoutTabKey: `/delivery-management/report`,
    },
    pathWithPage: {
      withoutTabKey: `/delivery-management/report?page=${page}`,
    },
  };

  const columns: any = [
    {
      title: "#",
      dataIndex: "index",
      render: (text: any, record: any, index: number) =>
        (page - 1) * itemPerPage + (index + 1),
    },
    {
      title: t("label.date").toLocaleUpperCase(),
      dataIndex: "createdAt",
      render: (createdAt: string) => {
        return createdAt
          ? moment(createdAt).format("DD-MM-YYYY | hh:mm a")
          : "-";
      },
    },
    {
      title: t("label.trainLine").toLocaleUpperCase(),
      dataIndex: ["trainLineData", "name"],
    },
    {
      title: t("label.journeyCode").toLocaleUpperCase(),
      dataIndex: "journeyCode",
      render: (journeyCode: string, record: any) =>
        journeyCode ? (
          <a
            className="clickable-id"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              routeToEnrouteView(record.trainPlanID, location, history);
            }}
          >
            {journeyCode}
          </a>
        ) : (
          <a>{"-"}</a>
        ),
    },
    {
      title: t("label.trainPlate").toLocaleUpperCase(),
      dataIndex: ["plateNo"],
    },
    {
      title: t("label.startStation").toLocaleUpperCase(),
      dataIndex: ["startStation"],
      render: (text: any, record: any) => {
        return (
          <Row className="margin-0-overwrite">
            <Col span={24}>{text}</Col>
            <Col span={24}>
              {record?.trainLineData?.stations?.map(
                (station) =>
                  station?.stationCode == text && station?.stationName,
              )}
            </Col>
          </Row>
        );
      },
    },
    {
      title: t("label.lastStation").toLocaleUpperCase(),
      dataIndex: ["lastStation"],
      render: (text: any, record: any) => {
        return (
          <Row className="margin-0-overwrite">
            <Col span={24}>{text}</Col>
            <Col span={24}>
              {record?.trainLineData?.stations?.map(
                (station) =>
                  station?.stationCode == text && station?.stationName,
              )}
            </Col>
          </Row>
        );
      },
    },
    {
      title: t("label.plannedDeparture").toLocaleUpperCase(),
      dataIndex: "plannedDeparture",
      render: (plannedDeparture: string) => {
        return plannedDeparture
          ? moment(plannedDeparture).format("DD-MM-YYYY | hh:mm a")
          : "-";
      },
    },
    {
      title: t("label.actualDeparture").toLocaleUpperCase(),
      dataIndex: "actualDeparture",
      render: (actualDeparture: string) => {
        return actualDeparture
          ? moment(actualDeparture).format("DD-MM-YYYY | hh:mm a")
          : "-";
      },
    },
    /*{
            title: t("label.diolkoStations").toLocaleUpperCase(),
            dataIndex: ["trainPlanData", "trainStops"],
            render: (_: any, record: any) => {
                return (
                    map(record.trainPlanData?.trainStops, (stops: any) => {
                        return (
                            <span>{stops?.stationCode}<br /></span>
                        )
                    })
                );
            },
        },
        {
            title: t("label.estimatedArrival").toLocaleUpperCase(),
            dataIndex: "estimatedArrival",
        },
        {
            title: t("label.actualArrival").toLocaleUpperCase(),
            dataIndex: "actualArrival",
        },
        {
            title: t("label.trolleysIn").toLocaleUpperCase(),
            dataIndex: "trolleysIn",
        },
        {
            title: t("label.trolleysInTime").toLocaleUpperCase(),
            dataIndex: "trolleysInTime",
        },
        {
            title: t("label.trolleysOut").toLocaleUpperCase(),
            dataIndex: "trolleysOut"
        },
        {
            title: t("label.trolleysOutTime").toLocaleUpperCase(),
            dataIndex: "trolleysOutTime",
        },*/
    {
      title: t("label.trainStatus").toLocaleUpperCase(),
      dataIndex: ["status"],
      render: (text: any) => {
        return (
          <span className="status-indicator" style={getStatusStyle(text)}>
            {text ? toCapitalisedSpaced(text) : ""}
          </span>
        );
      },
    },
  ];

  const innerColumns: any = [
    {
      title: t("label.diolkoStations").toLocaleUpperCase(),
      dataIndex: ["stationCode"],
    },
    {
      title: t("label.estimatedArrival").toLocaleUpperCase(),
      dataIndex: "estimatedArrival",
      render: (estimatedArrival: string) => {
        return estimatedArrival
          ? moment(estimatedArrival).format("DD-MM-YYYY | hh:mm a")
          : "-";
      },
    },
    {
      title: t("label.actualArrival").toLocaleUpperCase(),
      dataIndex: "actualArrival",
      render: (actualArrival: string) => {
        return actualArrival
          ? moment(actualArrival).format("DD-MM-YYYY | hh:mm a")
          : "-";
      },
    },
    {
      title: t("label.trolleysIn").toLocaleUpperCase(),
      dataIndex: "loadTrolleys",
      render: (trolleys: any, record: any) => {
        const { trolleyParcels } = trolleys || {};
        const { trolleyParcelData } = record || {};

        return constructTrolleyCode(trolleyParcels, trolleyParcelData);
      },
    },
    {
      title: t("label.trolleysInTime").toLocaleUpperCase(),
      dataIndex: "loadTrolleys",
      render: (trolleys: any, record: any) => {
        const { trolleyParcels } = trolleys || {};

        if (!isArray(trolleyParcels)) return "-";

        return trolleyParcels.map((parcel, index) => {
          const dateString = formatDate(parcel?.loadAt, "DD-MM-YYYY hh:mm a");

          return <div key={index}>{dateString ?? "-"}</div>;
        });
      },
    },
    {
      title: t("label.trolleysOut").toLocaleUpperCase(),
      dataIndex: "unloadTrolleys",
      render: (trolleys: any, record: any) => {
        const { trolleyParcels } = trolleys || {};
        const { trolleyParcelData } = record || {};

        return constructTrolleyCode(trolleyParcels, trolleyParcelData);
      },
    },
    {
      title: t("label.trolleysOutTime").toLocaleUpperCase(),
      dataIndex: "unloadTrolleys",
      render: (trolleys: any, record: any) => {
        const { trolleyParcels } = trolleys || {};

        if (!isArray(trolleyParcels)) return "-";

        return trolleyParcels.map((parcel, index) => {
          const dateString = formatDate(parcel?.loadAt, "DD-MM-YYYY hh:mm a");

          return <div key={index}>{dateString ?? "-"}</div>;
        });
      },
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "status",
      render: (text: string) => (
        <span className="status-indicator" style={getStatusStyle(text)}>
          {text ? toCapitalisedSpaced(text) : ""}
        </span>
      ),
    },
  ];

  const statusOptions = [
    { label: t("option.all"), value: "" },
    { label: "Planned", value: "PLANNED" },
    { label: "Ongoing", value: "ONGOING" },
    { label: "Completed", value: "COMPLETED" },
  ];

  const sortByCreatedAt = [
    { label: t("option.latest"), value: "DESC" },
    { label: t("option.earliest"), value: "ASC" },
  ];

  const expandable = {
    expandIcon: ({ expanded, onExpand, record }) =>
      expanded ? (
        <CaretUpOutlined onClick={(e) => onExpand(record, e)} />
      ) : (
        <CaretDownOutlined onClick={(e) => onExpand(record, e)} />
      ),
    expandedRowRender: (record: any) => {
      return (
        <div className="">
          <DataTable
            width={50}
            columns={innerColumns}
            dataSource={record.trainStops || []}
          />
        </div>
      );
    },
  };

  const handleFilterChange = (value: any, name: string) => {
    let selectedValue;

    switch (name) {
      case "dateFrom":
        selectedValue = value ? value.startOf("day").toISOString() : null;
        break;
      case "dateTo":
        selectedValue = value ? value.endOf("day").toISOString() : null;
        break;
      case "onlyLatestStatus":
        selectedValue = value ? "true" : "false"; // boolean will be cleared by the hook
        break;
      default:
        selectedValue = value;
    }

    if (Array.isArray(value)) {
      selectedValue = value.filter((item) => item !== "all");
    }
    setFilterState({ ...filterState, [name]: selectedValue });
  };

  return (
    <div>
      <div className="dm-report-sort-container">
        <ReportFilters
          handleFilterChange={handleFilterChange}
          filterState={filterState}
          handleDownloadExcelFile={handleDownloadExcelFile}
          excelType={"train"}
          reportListing={reportListing}
          statusOptions={statusOptions}
          sortByCreatedAt={sortByCreatedAt}
          handleRefreshList={handleRefreshList}
          isLoading={isLoading}
          isExcelLoading={isExcelLoading}
        />
        <Divider />
        <DataTableFilter
          searchPlaceholder={t("label.searchReportingTrain")}
          onSearch={(text: string) => handleSearch(text)}
        />
      </div>
      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
      ) : (
          <DataTable
            rowKey={(record: any) => {
              return record._id;
            }}
            expandable={expandable}
            columns={columns}
            scroll={{ x: "max-content" }}
            dataSource={reportListing}
          />
      )}
    </div>
  );
};

export default TrainReportList;
